"use client"

import { ChangeEvent, useMemo, useState } from "react"
import useOrientation from "@/hooks/useOrientation"

type Props = {
  value: number
  minvalue: number
  maxvalue: number
  onSeek: (value: number) => void
  waiting?: boolean
}

export default function RangeSlider({
  value,
  minvalue,
  maxvalue,
  onSeek,
  waiting = false,
}: Props) {

  const [isDragging, setIsDragging] = useState(false)

  const { isPortrait, isLandscape } = useOrientation()

  const percentage = useMemo(() => {
    if (maxvalue <= minvalue) return 0

    return (
      ((value - minvalue) /
        (maxvalue - minvalue)) *
      100
    )
  }, [
    value,
    minvalue,
    maxvalue,
  ])

  const onChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    onSeek(Number(e.target.value))
  }

  const hitArea =
    isPortrait
      ? 0
      : 28

  const trackHeight =
    isLandscape
      ? isDragging
        ? 7 //6
        : 4 //2
      : isDragging
        ? 5
        : 3

  return (
    <div
      className="
        relative
        flex
        w-full
        items-center
        select-none
        touch-none
        transform-gpu
      "
      style={{
        height: `${hitArea}px`,
      }}
    >

      {/* TRACK */}
      <div
        className="
          absolute
          inset-x-0
          overflow-hidden
          rounded-full
        "
        style={{
          height: `${trackHeight}px`,
          background: "rgba(255,255,255,.12)",
        }}
      >

        {/* Fondo */}
        <div
          className="
            absolute
            inset-0
            rounded-full
            bg-white/[0.12]
          "/>

        {/* OLA DE CARGA */}
        {waiting && (
          <>
            <style>
              {`
                @keyframes spotify-loading-wave {
                  0% {
                    transform: translateX(-120%);
                  }
              
                  100% {
                    transform: translateX(320%);
                  }
                }
              `}
            </style>
              
            <div
              className="
                absolute
                inset-y-0
                w-[60%]
                pointer-events-none
                transform-gpu
              "
              style={{
                animation:
                  "spotify-loading-wave 2.5s linear infinite",
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,.15) 20%, rgba(255,255,255,.65) 50%, rgba(255,255,255,.15) 80%, transparent 100%)",
                filter:
                  "blur(.6px)",
                mixBlendMode:
                  "screen",
                zIndex: 5,
              }}
            />
          </>
        )}

        

                {/* BARRA DE PROGRESO */}
        <div
          className="
            absolute
            left-0
            top-0
            h-full
            rounded-full
            overflow-hidden
            transform-gpu
          "
          style={{
            width: `${Math.min(
              100,
              Math.max(0, percentage)
            )}%`,
            background: isDragging
              ? "rgba(255,255,255,1)"
              : waiting
                ? "rgba(255,255,255,.40)"
                : "rgba(255,255,255,.82)",

            transition: waiting
              ? "background-color .25s ease"
              : "width .1s ease, background-color .2s ease",

            boxShadow: isDragging
              ? "0 0 12px rgba(255,255,255,.12)"
              : "none",
          }}
        >
          
        </div>

      </div>

      <input
        type="range"
        min={minvalue}
        max={maxvalue}
        step="0.01"
        value={value}
        onChange={onChange}
        onPointerDown={() => setIsDragging(true)}
        onPointerUp={() => setIsDragging(false)}
        onPointerCancel={() => setIsDragging(false)}
        onBlur={() => setIsDragging(false)}
        className="
          absolute
          inset-0
          z-50
          w-full
          h-full
          opacity-0
          cursor-pointer
          appearance-none
          bg-transparent
          focus:outline-none
          focus:ring-0
          touch-none
        "
      />

    </div>
  )
}